import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { ApplicationProductList } from "../../../models/product/application-product-list";
import { ApplicationError } from "../../../models/errors/application-error";
import AdminProductService from "../../../services/product/admin-product-service";
import { ApplicationDepartment } from "../../../models/organisation/application-department";
import DepartmentService from "../../../services/organisation/department-service";
import { ApplicationProductDetail } from "../../../models/product/application-product-detail";
import { ApplicationProduct } from "../../../models/product/application-product";
import { FetchParams } from "../../../components/controls/table-view";
import { ApplicationSubDepartment } from "../../../models/organisation/application-sub-department";
import { ApplicationProductUpload } from "../../../models/product/application-product-upload";

export const fetchProductsBySubDepartment = createAsyncThunk<
  ApplicationProductList | null,
  { subDepartmentId: number; fetchParams: FetchParams },
  { state: RootState }
>(
  "manageProduct/fetchBySubDepartment",
  async ({ subDepartmentId, fetchParams }, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const adminProductService = new AdminProductService();

    try {
      var currentPosition: number =
        state.adminProduct.products?.currentPosition ?? 0;
      var fetchNext: number = 15;
      var count: number = state.adminProduct.products?.count ?? -1;

      if (fetchParams.reset) {
        dispatch(resetProducts());
        currentPosition = 0;
        count = -1;
      }

      if (currentPosition !== count) {
        return await adminProductService.getProductsBySubDepartment(
          subDepartmentId,
          currentPosition,
          fetchNext,
          fetchParams.searchText ?? ""
        );
      }

      return null;
    } catch (error: any) {
      const apiError = ApplicationError.handleApiError(error, {});
      return rejectWithValue(apiError);
    }
  }
);

export const fetchDepartmentById = createAsyncThunk<
  ApplicationDepartment,
  number,
  { state: RootState }
>("departments/fetchById", async (departmentId, { rejectWithValue }) => {
  const departmentService = new DepartmentService();
  try {
    return await departmentService.getDepartmentById(departmentId);
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const fetchProductById = createAsyncThunk<
  ApplicationProductDetail | null,
  number,
  { state: RootState }
>("manageProduct/getSingle", async (productId, { rejectWithValue }) => {
  const adminProductService = new AdminProductService();

  try {
    return await adminProductService.getProductById(productId);
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});
export const updateManageProduct = createAsyncThunk<
  ApplicationProduct | null,
  ApplicationProductDetail,
  { state: RootState }
>("manageProduct/update", async (product, { rejectWithValue }) => {
  const adminProductService = new AdminProductService();

  try {
    return await adminProductService.updateManageProduct(product);
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const insertManageProduct = createAsyncThunk<
  ApplicationProduct | null,
  ApplicationProductDetail,
  { state: RootState }
>("manageProduct/insert", async (product, { rejectWithValue }) => {
  const adminProductService = new AdminProductService();

  try {
    return await adminProductService.insertManageProduct(product);
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});


export const deleteProduct = createAsyncThunk<
  void,
  number,
  { state: RootState }
>("manageProduct/insert", async (productId, { rejectWithValue }) => {
  const adminProductService = new AdminProductService();

  try {
    await adminProductService.deleteProduct(productId);
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const bulkUploadProducts = createAsyncThunk<
  ApplicationProductList | null,
  {
    departmentId: number;
    subDepartmentId: number;
    products: ApplicationProductUpload[];
  },
  { state: RootState }
>("manageProduct/bulkUpload", async (payload, { rejectWithValue }) => {
  const productService = new AdminProductService();

  try {
    const response = await productService.bulkUploadProducts(
      payload.departmentId,
      payload.subDepartmentId,
      payload.products,
      10
    );
    return response;
  } catch (error) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError || "Failed to upload products");
  }
});


export const updateProductOrder = createAsyncThunk<
  ApplicationProduct | null,
  UpdateProductPayload,
  { state: RootState }>
  ("manageProduct/updateOrder", async (payload, { getState, rejectWithValue }) => {
    const adminProductService = new AdminProductService();
    try {
      const { id, field, value } = payload;
      const state = getState();
      const product = state.adminProduct.products?.products.find(x => x.id === id);

      const updateProduct = {
        ...product,
        [field]: value
      }

      if (!updateProduct) {
        throw new ApplicationError("No product to update")
      }

      return await adminProductService.updateOrder(updateProduct.id!, updateProduct.order ?? 0);
    } catch (error: any) {
      const apiError = ApplicationError.handleApiError(error, {});
      return rejectWithValue(apiError);
    }
  })

interface AdminProductState {
  products: ApplicationProductList | null;
  selectedProduct: ApplicationProductDetail | null;
  selectedDepartment: ApplicationDepartment | null;
  selectedSubDepartment: ApplicationSubDepartment | null;
}

export interface UpdateProductPayload {
  id: number,
  field: keyof ApplicationProduct;
  value: any;
}
const initialState: AdminProductState = {
  products: null,
  selectedProduct: null,
  selectedDepartment: null,
  selectedSubDepartment: null,
};

const adminProductSlice = createSlice({
  name: "adminProduct",
  initialState,
  reducers: {
    resetProducts: (state) => {
      state.products = initialState.products;
    },
    removeProduct: (state, action: PayloadAction<number>) => {
      if (!state.products) return;

      state.products.products = state.products.products.filter(
        x => x.id !== action.payload
      );

      state.products.count -= 1;
    },
    resetSelectedProduct: (state) => {
      state.selectedProduct = initialState.selectedProduct;
    },
    setSelectedDepartment: (state, action: PayloadAction<ApplicationDepartment | null>) => {
      state.selectedDepartment = action.payload;
    },
    setSelectedSubDepartment: (state, action: PayloadAction<ApplicationSubDepartment | null>) => {
      state.selectedSubDepartment = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchProductsBySubDepartment.fulfilled,
        (state, action: PayloadAction<ApplicationProductList | null>) => {
          if (action.payload) {
            if (state.products?.products) {
              state.products.products = state.products.products.concat(
                action.payload.products
              );
              state.products.currentPosition = action.payload.currentPosition;
              state.products.count = action.payload.count;
            } else {
              state.products = action.payload;
            }
          }
        }
      )
    builder
      .addCase(
        bulkUploadProducts.fulfilled,
        (state, action: PayloadAction<ApplicationProductList | null>) => {
          if (action.payload) {
            state.products = action.payload;
          }
        }
      )
      .addCase(
        fetchProductById.fulfilled,
        (state, action: PayloadAction<ApplicationProductDetail | null>) => {
          if (action.payload) {
            state.selectedProduct = action.payload;
          }
        }
      )
      .addCase(
        updateManageProduct.fulfilled,
        (state, action: PayloadAction<ApplicationProduct | null>) => {
          if (action.payload && state.products?.products) {
            state.products.products = state.products.products.map((product) =>
              product.id === action.payload!.id
                ? (action.payload as ApplicationProduct)
                : product
            );
          }
        }
      )
      .addCase(
        updateProductOrder.fulfilled,
        (state, action: PayloadAction<ApplicationProduct | null>) => {
          const updated = action.payload;

          if (!updated || !state.products?.products) return;

          const products = [...state.products.products];

          const movedProductIndex = products.findIndex((c) => c.id === updated.id);
          if (movedProductIndex === -1) return;

          const [movedProduct] = products.splice(movedProductIndex, 1);

          if (!movedProduct) return;
          const newOrder = Math.max(1, Math.min(updated.order, products.length + 1));

          products.splice(newOrder - 1, 0, movedProduct);

          state.products.products = products.map((prd, index) => ({
            ...prd,
            order: index + 1,
          }));
        }
      )
      .addCase(
        insertManageProduct.fulfilled,
        (state, action: PayloadAction<ApplicationProduct | null>) => {
          if (action.payload && state.products?.products) {
            state.products.products.unshift(action.payload);
            state.products.count += 1;
            state.products.currentPosition += 1;
          }
        }
      );
  },
});

export const { resetProducts, resetSelectedProduct, removeProduct, setSelectedDepartment, setSelectedSubDepartment } =
  adminProductSlice.actions;
export default adminProductSlice.reducer;
